<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -180 400 360" class="canvas-svg">
          <!-- Axes and Circle -->
          <circle cx="0" cy="0" r="100" fill="rgba(13, 104, 255, 0.1)" stroke="#0D68FF" stroke-width="2" />
          <circle cx="0" cy="0" r="4" fill="#64748b" />
          <text x="-15" y="15" fill="#94a3b8" font-size="14" font-weight="bold">O</text>

          <!-- Central Angle AOB area -->
          <path :d="`M 0 0 L ${ptA.x} ${ptA.y} A 100 100 0 0 ${angleA > angleB ? 0 : 1} ${ptB.x} ${ptB.y} Z`" fill="rgba(42, 245, 152, 0.15)" />
          
          <line x1="0" y1="0" :x2="ptA.x" :y2="ptA.y" stroke="#2AF598" stroke-width="2" />
          <line x1="0" y1="0" :x2="ptB.x" :y2="ptB.y" stroke="#2AF598" stroke-width="2" />
          
          <circle :cx="ptA.x" :cy="ptA.y" r="5" fill="#2AF598" />
          <circle :cx="ptB.x" :cy="ptB.y" r="5" fill="#2AF598" />
          <text :x="ptA.x * 1.2" :y="ptA.y * 1.2 + 5" fill="#2AF598" font-weight="bold" font-size="16" text-anchor="middle">A</text>
          <text :x="ptB.x * 1.2" :y="ptB.y * 1.2 + 5" fill="#2AF598" font-weight="bold" font-size="16" text-anchor="middle">B</text>

          <!-- Inscribed Angle APB -->
          <polygon :points="`${ptA.x},${ptA.y} ${ptP.x},${ptP.y} ${ptB.x},${ptB.y}`" fill="rgba(244, 63, 94, 0.05)" stroke="#f43f5e" stroke-width="1.5" stroke-dasharray="4" />
          <circle :cx="ptP.x" :cy="ptP.y" r="5" fill="#f43f5e" />
          <text :x="ptP.x * 1.25" :y="ptP.y * 1.25 + 5" fill="#f43f5e" font-weight="bold" font-size="16" text-anchor="middle">P</text>
          
          <!-- Tangent Line at A -->
          <line v-if="showTangent" 
            :x1="ptA.x - ptA.y*1.2" :y1="ptA.y + ptA.x*1.2" 
            :x2="ptA.x + ptA.y*1.2" :y2="ptA.y - ptA.x*1.2" 
            stroke="#facc15" stroke-width="2" />
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             圆周角 ∠APB: {{ (centralAngle/2).toFixed(1) }}° &nbsp;|&nbsp; 圆心角 ∠AOB: {{ centralAngle.toFixed(1) }}°
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item">
                     <label>游动圆周点 P</label>
                     <el-slider v-model="angleP" :min="0" :max="360" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>定点 A</label>
                     <el-slider v-model="angleA" :min="0" :max="360" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>定点 B</label>
                     <el-slider v-model="angleB" :min="0" :max="360" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>显示切线 (弦切角)</label>
                     <el-switch v-model="showTangent" active-text="显示" inactive-text=""></el-switch>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>互动观察：</strong>无论动点 P 在圆周同侧如何移动，<strong>同弧所对的圆周角大小恒为对应圆心角的一半</strong>。若开启切线，弦与切线夹角（弦切角）也等于相应的圆周角。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CirclePropertiesViz',
  data() {
    return {
      angleA: 45,
      angleB: 135,
      angleP: 270,
      showTangent: false
    }
  },
  computed: {
    ptA() { return this.getPolar(this.angleA, 100); },
    ptB() { return this.getPolar(this.angleB, 100); },
    ptP() { return this.getPolar(this.angleP, 100); },
    centralAngle() {
       let diff = Math.abs(this.angleA - this.angleB);
       if (diff > 180) diff = 360 - diff;
       return diff;
    }
  },
  methods: {
    getPolar(deg, r) {
      let rad = deg * Math.PI / 180;
      return { x: r * Math.cos(rad), y: -r * Math.sin(rad) };
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { 
  flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .canvas-svg { width: 100%; height: 100%; }
  .hud-center {
    position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center;
    .status-badge {
      background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px);
      &.con { background: rgba(13,104,255,0.2); border-color: #0D68FF; color: #60a5fa; }
    }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
